<template>
  <div class="sig-pad" :class="`sig-pad--${size}`">
    <div class="sig-pad__frame">
      <canvas
        ref="canvasRef"
        class="sig-pad__canvas"
        @pointerdown="onDown"
        @pointermove="onMove"
        @pointerup="onUp"
        @pointerleave="onUp"
        @pointercancel="onUp"
      />
      <p v-if="empty" class="sig-pad__placeholder" aria-hidden="true">
        마우스나 손가락으로 서명해 주세요
      </p>
    </div>
    <div class="sig-pad__tools">
      <button type="button" class="sui-btn sui-btn--sm" @click="clear">지우기</button>
      <span class="sig-pad__status" :class="{ 'is-done': !empty }">
        {{ empty ? '서명이 비어 있습니다' : '서명 입력됨' }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

withDefaults(
  defineProps<{
    size?: 'md' | 'lg'
  }>(),
  { size: 'lg' },
)

const emit = defineEmits<{
  (e: 'change', dataUrl: string): void
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const empty = ref(true)
let drawing = false
let ctx: CanvasRenderingContext2D | null = null
let savedDataUrl = ''

function resize(preserve = true) {
  const canvas = canvasRef.value
  if (!canvas) return
  const keep = preserve && !empty.value ? canvas.toDataURL('image/png') : ''
  const ratio = window.devicePixelRatio || 1
  const width = canvas.clientWidth
  const height = canvas.clientHeight
  canvas.width = Math.floor(width * ratio)
  canvas.height = Math.floor(height * ratio)
  ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
  ctx.lineWidth = 2.8
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#1b1f24'
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, width, height)

  if (keep) {
    const img = new Image()
    img.onload = () => {
      if (!ctx) return
      ctx.drawImage(img, 0, 0, width, height)
      empty.value = false
      savedDataUrl = keep
      emit('change', keep)
    }
    img.src = keep
    return
  }

  empty.value = true
  savedDataUrl = ''
  emit('change', '')
}

function point(event: PointerEvent) {
  const canvas = canvasRef.value
  if (!canvas) return { x: 0, y: 0 }
  const rect = canvas.getBoundingClientRect()
  return { x: event.clientX - rect.left, y: event.clientY - rect.top }
}

function onDown(event: PointerEvent) {
  if (!ctx) return
  event.preventDefault()
  drawing = true
  canvasRef.value?.setPointerCapture(event.pointerId)
  const p = point(event)
  ctx.beginPath()
  ctx.moveTo(p.x, p.y)
}

function onMove(event: PointerEvent) {
  if (!drawing || !ctx) return
  event.preventDefault()
  const p = point(event)
  ctx.lineTo(p.x, p.y)
  ctx.stroke()
  empty.value = false
}

function onUp() {
  if (!drawing) return
  drawing = false
  emitChange()
}

function emitChange() {
  const canvas = canvasRef.value
  if (!canvas || empty.value) {
    savedDataUrl = ''
    emit('change', '')
    return
  }
  savedDataUrl = canvas.toDataURL('image/png')
  emit('change', savedDataUrl)
}

function clear() {
  resize(false)
}

function onWindowResize() {
  resize(true)
}

onMounted(() => {
  resize(false)
  window.addEventListener('resize', onWindowResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize)
})

defineExpose({ clear, isEmpty: () => empty.value })
</script>

<style lang="scss" src="./SignaturePad.scss"></style>
